/* ═══════════════════════════════════════════════════════════════
   Grid components — DayHeaderCell, GroupHeader, MemberRow, DayCell,
   CapacityCell. These are the visual cells that compose the weekly
   forecasting grid. All keyboard-accessible (role=button, Enter/Space)
   and ARIA-labeled.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { TeamMember, HourForecast, Project } from '../../../shared/types';
import {
  type MemberCapacity,
  headerCell,
  projectColor,
  todayIso,
  VACATION_COLOR,
  VACATION_PROJECT_LABEL,
} from './_shared';

// ─── Day header cell ─────────────────────────────────────────────────

export function DayHeaderCell({
  day,
  isToday,
  showWeekend,
}: {
  day: string;
  isToday: boolean;
  showWeekend: boolean;
}) {
  const [y, m, d] = day.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dt.getUTCDay()];
  const isWeekend = dt.getUTCDay() === 0 || dt.getUTCDay() === 6;
  return (
    <div
      style={{
        ...headerCell,
        textAlign: 'center',
        background: isToday
          ? 'color-mix(in srgb, var(--ac) 8%, var(--sf))'
          : isWeekend
            ? 'var(--sa)'
            : 'var(--sf)',
        color: isToday ? 'var(--ac)' : isWeekend && !showWeekend ? 'var(--tm)' : 'var(--tx)',
        opacity: isWeekend && !showWeekend ? 0.4 : 1,
        position: 'relative',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
        }}
      >
        {dayOfWeek}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{d}</div>
      {isToday && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 24,
            height: 2,
            background: 'var(--ac)',
            borderRadius: 1,
          }}
        />
      )}
    </div>
  );
}

// ─── Group header row ────────────────────────────────────────────────

export function GroupHeader({
  group,
  collapsed,
  onToggle,
}: {
  group: { id: string; label: string; color?: string; members: TeamMember[] };
  collapsed: boolean;
  onToggle: () => void;
}) {
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={!collapsed}
      aria-label={`${group.label} group, ${group.members.length} members, ${collapsed ? 'collapsed' : 'expanded'}`}
      onClick={onToggle}
      onKeyDown={handleKey}
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: 'var(--sa)',
        borderTop: '1px solid var(--bd)',
        borderBottom: '1px solid var(--bd)',
        cursor: 'pointer',
        position: 'sticky',
        top: 38,
        zIndex: 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          fontSize: 10,
          color: 'var(--tm)',
          width: 10,
          transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          transition: 'transform .12s',
        }}
      >
        ▶
      </span>
      {group.color && (
        <span
          aria-hidden="true"
          style={{ width: 12, height: 12, borderRadius: 3, background: group.color, flexShrink: 0 }}
        />
      )}
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--tx)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {group.label}
      </span>
      <span style={{ fontSize: 11, color: 'var(--tm)' }}>({group.members.length})</span>
    </div>
  );
}

// ─── Member row (7 day cells + capacity bar) ─────────────────────────

export function MemberRow({
  member,
  weekDays,
  forecastsByDay,
  projects,
  capacity,
  dailyTarget,
  canEdit,
  onCellClick,
}: {
  member: TeamMember;
  weekDays: string[];
  forecastsByDay: Map<string, HourForecast[]>;
  projects: Project[];
  capacity: MemberCapacity;
  dailyTarget: number;
  canEdit: boolean;
  onCellClick: (dayDate: string, forecastId?: string) => void;
}) {
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name || 'Unknown';

  return (
    <>
      {/* Member name cell */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--bd)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'var(--sf)',
          position: 'sticky',
          left: 0,
          zIndex: 1,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--ac)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {member.name
            .split(/\s+/)
            .map((p) => p[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--tx)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {member.name}
          </div>
          <div style={{ fontSize: 10, color: 'var(--tm)' }}>
            {capacity.isPartTime ? 'Part-time' : 'Full-time'} · {capacity.rawTarget}h/wk
            {capacity.vacationHours > 0 && (
              <>
                {' '}
                · <span style={{ color: '#6b7280' }}>−{capacity.vacationHours}h vac</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Day cells */}
      {weekDays.map((day) => {
        const items = forecastsByDay.get(day) || [];
        const cellTotal = items.reduce((s, f) => s + (Number(f.hours) || 0), 0);
        return (
          <DayCell
            key={day}
            day={day}
            items={items}
            total={cellTotal}
            dailyTarget={dailyTarget}
            projectName={projectName}
            canEdit={canEdit}
            onClick={() => onCellClick(day)}
            onItemClick={(forecastId) => onCellClick(day, forecastId)}
          />
        );
      })}

      {/* Capacity bar */}
      <CapacityCell capacity={capacity} />
    </>
  );
}

// ─── Day cell ────────────────────────────────────────────────────────

export function DayCell({
  day,
  items,
  total,
  dailyTarget,
  projectName,
  canEdit,
  onClick,
  onItemClick,
}: {
  day: string;
  items: HourForecast[];
  total: number;
  dailyTarget: number;
  projectName: (id: string) => string;
  canEdit: boolean;
  onClick: () => void;
  onItemClick: (forecastId: string) => void;
}) {
  const isToday = day === todayIso();
  const [y, m, d] = day.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const isWeekend = dt.getUTCDay() === 0 || dt.getUTCDay() === 6;
  const [isHovered, setIsHovered] = React.useState(false);
  // Day-level over-allocation flag. 5% headroom for rounding (8.5h total
  // when the daily target rounds to 8h shouldn't ring red).
  const overDay = dailyTarget > 0 && total > dailyTarget * 1.05;

  // Cell click ALWAYS opens the popover — read-only mode renders an
  // explanatory banner when the user lacks edit perms. The silent no-op
  // bug from v2.7.x must never come back.
  const handleClick = () => onClick();
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${day} — ${total}h booked${overDay ? ', over daily capacity' : ''}`}
      onClick={handleClick}
      onDoubleClick={handleClick}
      onKeyDown={handleKey}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: 4,
        borderBottom: '1px solid var(--bd)',
        borderLeft: '1px solid var(--bd)',
        background: isToday
          ? 'color-mix(in srgb, var(--ac) 4%, transparent)'
          : isWeekend
            ? 'var(--sa)'
            : isHovered && items.length === 0 && canEdit
              ? 'color-mix(in srgb, var(--ac) 5%, transparent)'
              : '',
        outline: overDay
          ? '1.5px solid var(--dg)'
          : isHovered && items.length === 0 && canEdit
            ? '1px dashed var(--ac)'
            : 'none',
        outlineOffset: -2,
        cursor: 'pointer',
        minHeight: 56,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'relative',
        transition: 'background .12s, outline .12s',
      }}
      title={
        overDay
          ? `Over daily capacity — ${total}h booked vs ~${dailyTarget.toFixed(1)}h available.`
          : canEdit
            ? items.length === 0
              ? 'Click to assign hours'
              : ''
            : "You don't have permission to edit this person's forecast hours — click to view"
      }
    >
      {/* "+ Add hours" affordance on hover for empty cells. */}
      {items.length === 0 && isHovered && canEdit && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ac)',
            fontSize: 11,
            fontWeight: 600,
            pointerEvents: 'none',
            opacity: 0.7,
          }}
        >
          + Add hours
        </div>
      )}
      {items.map((f) => {
        const isVacation = f.sourceKind === 'vacation';
        const color = isVacation ? VACATION_COLOR : projectColor(f.projectId);
        const label = isVacation ? VACATION_PROJECT_LABEL : projectName(f.projectId);
        return (
          <div
            key={f.id}
            onClick={(e) => {
              e.stopPropagation();
              if (!isVacation) onItemClick(f.id);
            }}
            style={{
              background: `color-mix(in srgb, ${color} 18%, transparent)`,
              border: `1px solid ${color}`,
              borderLeft: `3px solid ${color}`,
              color: 'var(--tx)',
              borderRadius: 4,
              padding: '2px 6px',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              cursor: canEdit && !isVacation ? 'pointer' : 'default',
              opacity: isVacation ? 0.85 : 1,
              minHeight: 22,
            }}
            title={`${label} — ${f.hours}h${isVacation ? ' (vacation, locked)' : ''}`}
          >
            {isVacation && <span style={{ fontSize: 10 }}>🏖</span>}
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
              {Number(f.hours)}h
            </span>
          </div>
        );
      })}
      {total > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 2,
            right: 4,
            fontSize: 9,
            color: 'var(--tm)',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            background: 'var(--bg)',
            padding: '0 3px',
            borderRadius: 3,
          }}
        >
          ∑ {total}h
        </div>
      )}
    </div>
  );
}

// ─── Capacity bar (right-most column) ────────────────────────────────

export function CapacityCell({ capacity }: { capacity: MemberCapacity }) {
  const { workedHours, availableHours, vacationHours, color, label } = capacity;
  // Bar measures *worked* hours against *available* (non-vacation) hours.
  // A week that's 100% vacation shows a neutral track with no fill.
  const denom = availableHours > 0 ? availableHours : 1;
  const pct = Math.min(150, (workedHours / denom) * 100);
  const isAllVacation = availableHours === 0 && vacationHours > 0;
  return (
    <div
      style={{
        padding: '10px 12px',
        borderBottom: '1px solid var(--bd)',
        borderLeft: '1px solid var(--bd)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        background: 'var(--sf)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span
          style={{ fontSize: 14, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}
          title={`${label} — ${workedHours.toFixed(1)}h worked${vacationHours > 0 ? `, ${vacationHours.toFixed(1)}h vacation` : ''}, ${availableHours.toFixed(1)}h available`}
        >
          {isAllVacation ? '—' : `${workedHours.toFixed(1)}h`}
        </span>
        <span style={{ fontSize: 10, color: 'var(--tm)' }}>
          {isAllVacation ? 'On vacation' : `/ ${availableHours.toFixed(0)}h`}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={Math.round(workedHours)}
        aria-valuemin={0}
        aria-valuemax={Math.max(1, Math.round(availableHours))}
        aria-label={`${label}: ${workedHours.toFixed(1)} of ${availableHours.toFixed(0)} available hours`}
        style={{
          height: 6,
          background: 'var(--sa)',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${Math.min(100, pct)}%`,
            background: color,
            transition: 'width .2s',
          }}
        />
        {pct > 100 && (
          <div
            style={{
              position: 'absolute',
              left: '100%',
              top: 0,
              bottom: 0,
              width: `${Math.min(50, pct - 100)}%`,
              background: '#ea580c',
              transform: 'translateX(-100%)',
              opacity: 0.6,
            }}
          />
        )}
      </div>
    </div>
  );
}
